import { FlexCol, FlexRow } from '@/src/components'
import { LocationTag } from '@/src/components/LocationTag'
import { SEUTag } from '@/src/components/SEUTag'
import { SearchBox } from '@/src/components/SearchBox/SearchBox'
import { useSeriesContext } from '@/src/contexts/SeriesContext'
import { useEnpis } from '@/src/features/iso-suite/enpi/hooks/useEnpis.hook'
import { useLocationOptions } from '@/src/features/iso-suite/measures/hooks/useLocationOptions.hook'
import { matchesEnpiSearchQuery } from '@/src/features/iso-suite/measures/mesaure-detail/enpi-search.utils'
import { isEffectivenessDateInvalidForEnpi } from '@/src/features/iso-suite/measures/mesaure-detail/measure-enpi-validation.utils'
import { appColors } from '@/src/theme/foundations/colors'
import type { EnPI } from '@/src/types/enpi.type'
import { Box, Spinner, Text, Tooltip } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle } from 'react-feather'
import { useTranslation } from 'react-i18next'

const TAG_W = '130px'

type EnpiPickerPanelProps = {
  currentEnpiId?: number | null
  selectedEnpiId?: number
  selectedEnpiIds?: number[]
  selectionMode?: 'single' | 'multiple'
  onChange?: (enpiId: number | undefined) => void
  onChangeMany?: (enpiIds: number[]) => void
  allowSingleDeselect?: boolean
  /**
   * Effectiveness date of the measure (ISO yyyy-MM-dd). When provided, EnPIs
   * whose `referenceDateTo >= effectivenessDate` are marked as incompatible
   * (disabled + amber warning tag). Pass `undefined` to disable this check.
   */
  effectivenessDate?: string | null
  /** Rendered when the user has no EnPIs at all. */
  emptyState?: React.ReactNode
}

type EnpiListItemProps = {
  enpi: EnPI
  isSelected: boolean
  isIncompatible: boolean
  onSelect: (id: number) => void
  seriesMap: Map<string, { title?: string }>
}

const EnpiListItem = ({ enpi, isSelected, isIncompatible, onSelect, seriesMap }: EnpiListItemProps) => {
  const { t } = useTranslation()
  let seuTitle = enpi.seuMetadata?.label ?? ''
  if (!seuTitle && enpi.seuId?.id) {
    seuTitle = seriesMap.get(enpi.seuId.id)?.title ?? ''
  }
  if (enpi.seuId.type === 'location_total') {
    seuTitle = t('enpi.seu_selector.overall_consumption')
  }

  const rowBg = isSelected ? appColors.accentPrimary : 'transparent'
  const rowColor = isSelected ? appColors.white : appColors.foreground
  const hoverBg = isIncompatible ? 'transparent' : isSelected ? appColors.accentPrimary : appColors.gray['200']
  const cursor = isIncompatible ? 'not-allowed' : 'pointer'
  const opacity = isIncompatible && !isSelected ? 0.55 : 1

  const row = (
    <FlexRow
      px='12px'
      py='6px'
      cursor={cursor}
      alignItems='center'
      gap='12px'
      borderRadius='8px'
      color={rowColor}
      bg={rowBg}
      opacity={opacity}
      _hover={{ bg: hoverBg }}
      onClick={() => {
        if (isIncompatible) return
        onSelect(enpi.id)
      }}
    >
      <Tooltip label={enpi.title} bg='white' color='gray.800' fontWeight='normal' p='2' borderRadius='base' hasArrow placement='top'>
        <Text flex='1' minW={0} fontSize='14px' fontWeight={isSelected ? 500 : 400} noOfLines={1}>
          {enpi.title}
        </Text>
      </Tooltip>

      <Box w={TAG_W} flexShrink={0} minW={0}>
        {enpi.locationId && <LocationTag truncate maxChars={14} locationId={enpi.locationId} width='100%' />}
      </Box>
      <Box w={TAG_W} flexShrink={0} minW={0}>
        {seuTitle && <SEUTag title={seuTitle} truncate maxChars={14} width='100%' />}
      </Box>
      {isIncompatible && (
        <Box flexShrink={0}>
          <AlertTriangle size={14} color='#B45309' />
        </Box>
      )}
    </FlexRow>
  )

  if (!isIncompatible) return row

  return (
    <Tooltip
      label={t('iso_measures_enpi_picker_incompatible_tooltip')}
      bg='white'
      color='gray.800'
      fontWeight='normal'
      p='2'
      borderRadius='base'
      hasArrow
      placement='top'
    >
      <Box>{row}</Box>
    </Tooltip>
  )
}

const LoadingState = () => (
  <Box py='40px' textAlign='center'>
    <Spinner />
  </Box>
)

const NoResultsState = () => {
  const { t } = useTranslation()
  return (
    <Box py='20px' textAlign='center'>
      <Text color={appColors.gray['500']} fontSize='14px'>
        {t('no_results')}
      </Text>
    </Box>
  )
}

/**
 * EC-5371: shared EnPI picker used by both the success-measurement tab
 * ("Entry A") and the documentation sidebar modal ("Entry B").
 *
 * Pure presentation + lifted state — never calls the backend to update
 * the measure. The parent decides what happens on confirm.
 *
 * When `effectivenessDate` is passed, EnPIs whose reference window ends
 * on or after that date are rendered as disabled with an amber warning
 * tag instead of surfacing the conflict as a toast later.
 */
export const EnpiPickerPanel = ({
  currentEnpiId,
  selectedEnpiId,
  selectedEnpiIds = [],
  selectionMode = 'single',
  onChange,
  onChangeMany,
  allowSingleDeselect = true,
  effectivenessDate,
  emptyState,
}: EnpiPickerPanelProps) => {
  const { t } = useTranslation()
  const { series } = useSeriesContext()
  const { data: enpis, isLoading } = useEnpis()
  const locationOptions = useLocationOptions()

  const seriesMap = useMemo(() => new Map(series.map(s => [s.id, s])), [series])
  const locationNameById = useMemo(() => new Map(locationOptions.map(location => [location.value, location.label])), [locationOptions])
  const selectedIds = useMemo(() => {
    if (selectionMode === 'multiple') return selectedEnpiIds
    return selectedEnpiId ? [selectedEnpiId] : []
  }, [selectedEnpiId, selectedEnpiIds, selectionMode])
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    setSearchTerm('')
  }, [currentEnpiId])

  const filteredEnpis = useMemo<EnPI[]>(() => {
    if (!enpis) return []

    let filtered = enpis
    if (searchTerm.trim()) {
      filtered = enpis.filter(enpi => {
        if (enpi.seuId.type !== 'location_total') {
          return matchesEnpiSearchQuery(searchTerm, enpi, locationNameById, seriesMap)
        }

        const localizedEnpi = {
          ...enpi,
          seuMetadata: {
            ...enpi.seuMetadata,
            label: t('enpi.seu_selector.overall_consumption'),
          },
        }
        return matchesEnpiSearchQuery(searchTerm, localizedEnpi, locationNameById, seriesMap)
      })
    }

    if (currentEnpiId) {
      return [...filtered].sort((a, b) => {
        if (a.id === currentEnpiId) return -1
        if (b.id === currentEnpiId) return 1
        return 0
      })
    }

    return filtered
  }, [enpis, searchTerm, currentEnpiId, locationNameById, seriesMap, t])

  const hasEnpis = !!enpis && enpis.length > 0

  const handleSelect = (id: number) => {
    if (selectionMode === 'multiple') {
      onChangeMany?.(selectedIdSet.has(id) ? selectedEnpiIds.filter(selectedId => selectedId !== id) : [...selectedEnpiIds, id])
      return
    }

    onChange?.(selectedEnpiId === id && allowSingleDeselect ? undefined : id)
  }

  const renderBody = () => {
    if (isLoading) return <LoadingState />
    if (!hasEnpis) return emptyState ?? <NoResultsState />
    if (filteredEnpis.length === 0) return <NoResultsState />

    return (
      <FlexCol gap='0' maxH='250px' overflowY='auto' mt='12px'>
        {filteredEnpis.map(enpi => {
          const isIncompatible = isEffectivenessDateInvalidForEnpi(effectivenessDate ?? undefined, enpi.id, enpis)
          return (
            <EnpiListItem
              key={enpi.id}
              enpi={enpi}
              isSelected={selectedIdSet.has(enpi.id)}
              isIncompatible={isIncompatible}
              onSelect={handleSelect}
              seriesMap={seriesMap}
            />
          )
        })}
      </FlexCol>
    )
  }

  return (
    <Box borderRadius='8px' border={`1px solid ${appColors.gray['100']}`}>
      <Box borderBottom={`1px solid ${appColors.gray['100']}`} p='6px'>
        <SearchBox value={searchTerm} onChange={setSearchTerm} />
      </Box>
      {renderBody()}
    </Box>
  )
}
